/**
 * Zapisane zestawy (4 polaroidy) — lustro lokalne `saved_outfits_json` (PLAN-V2 §5.2).
 * Serwer ma kolumnę, ale brak publicznego PUT w V1 — offline-first w SaveLocal.
 */
import { SaveLocal } from '../../core/SaveLocal.js';
import { scopedKey } from '../../core/profileScope.js';
import {
  emptyOutfitSlots,
  emptySwimSlots,
  normalizeOutfitSlots,
  normalizeSwimSlots,
  type OutfitSlots,
  type SwimSlots,
} from '../../avatar/parseOutfit.js';

export const SAVED_OUTFITS_KEY = 'pieskowo.saved_outfits_json';
export const SAVED_OUTFIT_SLOTS = 4 as const;

export type SavedOutfit = {
  slots: OutfitSlots;
  swimSlots: SwimSlots;
  /** Opcjonalna etykieta PL. */
  namePl?: string;
  /** Kolor ramki polaroidu. */
  frameColor?: number;
};

export type SavedOutfitSlot = SavedOutfit | null;

const FRAME_COLORS = [0xffb6c1, 0xbde0fe, 0xb8e6d0, 0xffe4a0] as const;

export function emptySavedSlots(): SavedOutfitSlot[] {
  return [null, null, null, null];
}

export function normalizeSavedOutfits(raw: unknown): SavedOutfitSlot[] {
  const out = emptySavedSlots();
  if (!Array.isArray(raw)) return out;
  for (let i = 0; i < SAVED_OUTFIT_SLOTS; i++) {
    const entry = raw[i];
    if (!entry || typeof entry !== 'object') {
      out[i] = null;
      continue;
    }
    const o = entry as Record<string, unknown>;
    const slots = normalizeOutfitSlots(
      (o.slots as Record<string, unknown>) ?? {},
    );
    const swimSlots = normalizeSwimSlots(
      (o.swimSlots as Record<string, unknown>) ??
        (o.swim as Record<string, unknown>) ??
        {},
    );
    // pusty zestaw = null
    const hasAny =
      Object.values(slots).some(Boolean) || Object.values(swimSlots).some(Boolean);
    if (!hasAny) {
      out[i] = null;
      continue;
    }
    out[i] = {
      slots,
      swimSlots,
      namePl: typeof o.namePl === 'string' ? o.namePl : `Zestaw ${i + 1}`,
      frameColor:
        typeof o.frameColor === 'number'
          ? o.frameColor
          : FRAME_COLORS[i % FRAME_COLORS.length],
    };
  }
  return out;
}

export function loadSavedOutfits(): SavedOutfitSlot[] {
  const raw = SaveLocal.getJson<unknown>(scopedKey(SAVED_OUTFITS_KEY), []);
  return normalizeSavedOutfits(raw);
}

export function persistSavedOutfits(slots: readonly SavedOutfitSlot[]): void {
  SaveLocal.setJson(scopedKey(SAVED_OUTFITS_KEY), slots);
}

/** Zapisz bieżący strój w slocie polaroidu (0–3). */
export function saveOutfitToSlot(
  index: number,
  slots: OutfitSlots,
  swimSlots: SwimSlots,
  current: readonly SavedOutfitSlot[] = loadSavedOutfits(),
): SavedOutfitSlot[] {
  const next = current.slice(0, SAVED_OUTFIT_SLOTS) as SavedOutfitSlot[];
  while (next.length < SAVED_OUTFIT_SLOTS) next.push(null);
  if (index < 0 || index >= SAVED_OUTFIT_SLOTS) return next;
  next[index] = {
    slots: { ...slots },
    swimSlots: { ...swimSlots },
    namePl: `Zestaw ${index + 1}`,
    frameColor: FRAME_COLORS[index % FRAME_COLORS.length],
  };
  persistSavedOutfits(next);
  return next;
}

/** Wyczyść polaroid. */
export function clearOutfitSlot(
  index: number,
  current: readonly SavedOutfitSlot[] = loadSavedOutfits(),
): SavedOutfitSlot[] {
  const next = current.slice(0, SAVED_OUTFIT_SLOTS) as SavedOutfitSlot[];
  while (next.length < SAVED_OUTFIT_SLOTS) next.push(null);
  if (index < 0 || index >= SAVED_OUTFIT_SLOTS) return next;
  next[index] = null;
  persistSavedOutfits(next);
  return next;
}

/** Snapshot do założenia z polaroidu. */
export function outfitFromSaved(s: SavedOutfit): {
  slots: OutfitSlots;
  swimSlots: SwimSlots;
} {
  return {
    slots: { ...s.slots },
    swimSlots: { ...s.swimSlots },
  };
}

/** Czy strój ma cokolwiek założonego. */
export function hasAnyEquipped(slots: OutfitSlots, swim: SwimSlots): boolean {
  return (
    Object.values(slots).some(Boolean) || Object.values(swim).some(Boolean)
  );
}

export function blankOutfit(): { slots: OutfitSlots; swimSlots: SwimSlots } {
  return { slots: emptyOutfitSlots(), swimSlots: emptySwimSlots() };
}
